import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Leaf, TrendingUp, TrendingDown, Trophy, Minus } from "lucide-react";
import { useLang } from "@/i18n";

const fmt = (iso, locale) => new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "short" });

const Stat = ({ icon: Icon, label, value, id }) => (
  <div data-testid={id} className="rounded-2xl bg-slate-50 p-4">
    <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-gray-500"><Icon className="h-3.5 w-3.5 text-emerald-600" />{label}</p>
    <p className="mt-1 font-display text-2xl font-bold text-gray-900">{value}</p>
  </div>
);

export const TisTracker = ({ trips }) => {
  const { t, locale } = useLang();
  const scored = trips.filter((x) => typeof x.tis === "number").sort((a, b) => a.created_at.localeCompare(b.created_at));
  if (scored.length === 0) return null;
  const avg = Math.round(scored.reduce((s, x) => s + x.tis, 0) / scored.length);
  const best = scored.reduce((b, x) => (x.tis > b.tis ? x : b), scored[0]);
  const delta = scored.length > 1 ? scored[scored.length - 1].tis - scored[scored.length - 2].tis : 0;
  const TrendIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const data = scored.map((x) => ({ name: fmt(x.created_at, locale), tis: x.tis, dest: x.destination }));

  return (
    <div data-testid="tis-tracker" className="mb-8 rounded-3xl border border-emerald-100 bg-white p-6 md:p-8">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-700"><Leaf className="h-4 w-4" />{t("Il tuo TS Impact Score nel tempo")}</p>
      <div className="mt-5 grid grid-cols-3 gap-3">
        <Stat id="tis-tracker-avg" icon={Leaf} label={t("Media")} value={`${avg}/100`} />
        <Stat id="tis-tracker-best" icon={Trophy} label={t("Migliore")} value={best.tis} />
        <Stat id="tis-tracker-trend" icon={TrendIcon} label={t("Ultimo viaggio")} value={scored[scored.length - 1].tis} />
      </div>
      {scored.length > 1 && (
        <p data-testid="tis-tracker-delta" className="mt-3 text-sm text-gray-600">
          {t("Rispetto al viaggio precedente:")} <strong className={delta > 0 ? "text-emerald-700" : delta < 0 ? "text-amber-700" : "text-gray-900"}>{t("{n} punti", { n: `${delta > 0 ? "+" : ""}${delta}` })}</strong>
        </p>
      )}
      {scored.length > 1 && (
        <div className="mt-6 h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <ReferenceLine y={avg} stroke="#a7f3d0" strokeDasharray="4 4" />
              <Tooltip formatter={(v) => [`${v}/100`, "TIS"]} labelFormatter={(l, p) => p?.[0]?.payload?.dest || l} />
              <Line type="monotone" dataKey="tis" stroke="#047857" strokeWidth={3} dot={{ r: 4, fill: "#10b981" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <p className="mt-3 text-xs text-gray-500">{t("Migliore: {d} · Segui i consigli \"Come fare ancora meglio\" per far salire il punteggio.", { d: best.destination })}</p>
    </div>
  );
};

const tone = (s) => (s >= 80 ? "bg-emerald-600 text-white" : s >= 60 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800");

export const TisBadge = ({ score, label, id }) => (
  <div data-testid={id} className={`shrink-0 rounded-2xl px-3 py-2 text-center ${tone(score)}`}>
    <p className="font-display text-2xl font-extrabold leading-none">{score}</p>
    <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider opacity-80">TIS /100</p>
    {label && <p className="sr-only">{label}</p>}
  </div>
);
